/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { BarChart } from "../../models/http/charts/bar-chart/bar-chart.model";
import { MixedChart } from "../../models/http/charts/mixed-chart/mixed-chart.model";
import { PieChart } from "../../models/http/charts/pie-chart/pie-chart.model";
import { TrendLine } from "../../models/http/charts/trend-line/trend-line.model";
import { HardwareAssetsRealtimeInformation } from "../../models/http/dashboard/hardware-assets-realtime-information.model";
import { ChartResponse } from "../../models/ui/charts.model";
import { CioMode } from "../../models/ui/cio-mode.model";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";
import { prettifyISODate } from "../../utils/utils";
import { CommunicationService } from "../communication.service";

@Injectable({
	providedIn: "root"
})
export class DashboardCioHttpService {
	private _dashboardCioOperations = ENDPOINT_GROUPS.dashboardCio.operations;

	constructor(private _communicationService: CommunicationService) {}

	/**
	 * Retrieves the summary for the specified time range.
	 *
	 * @param startDate - The start date for the time range.
	 * @param endDate - The end date for the time range.
	 * @returns An observable containing the summary data.
	 */
	public getSummary(
		startDate: string,
		endDate?: string
	): Observable<HardwareAssetsRealtimeInformation> {
		const paramsObject: Record<string, string> = { startDate };
		if (endDate) paramsObject["endDate"] = endDate;

		const params = new HttpParams({
			fromObject: paramsObject
		});

		return this._communicationService.callService(
			this._dashboardCioOperations.getSummary.service,
			HttpMethods.GET,
			{ params }
		);
	}

	/**
	 * Retrieves the forecast data for the specified time range and mode.
	 *
	 * @param startDate - The start date for the time range.
	 * @param endDate - The end date for the time range.
	 * @param mode - The mode in which the forecast data should be retrieved.
	 * @returns An Observable that emits the forecast data in the form of a MixedChart.
	 */
	public getForecast(
		startDate: string,
		endDate: string | undefined,
		mode: CioMode
	): Observable<ChartResponse<MixedChart>> {
		const paramsObject: Record<string, string> = {
			startDate,
			mode: this._mapModeToPieApi(mode)
		};
		if (endDate) paramsObject["endDate"] = endDate;

		const params = new HttpParams({
			fromObject: paramsObject
		});

		return this._communicationService
			.callService(this._dashboardCioOperations.getForecast.service, HttpMethods.GET, {
				params
			})
			.pipe(
				map((response: TrendLine) => ({
					chart: this._mapTrendLineToMixedChart(response),
					hasError: false
				})),
				catchError(() => of({ chart: {} as MixedChart, hasError: true }))
			);
	}

	/**
	 * Retrieves the emissions share data for the specified time range and mode.
	 *
	 * @param startDate - The start date for the time range.
	 * @param endDate - The end date for the time range.
	 * @param mode - The mode in which to retrieve emissions share data.
	 * @returns An Observable that emits the PieChart data representing the emissions share.
	 */
	public getEmissionsShare(
		startDate: string,
		endDate: string | undefined,
		mode: CioMode
	): Observable<ChartResponse<PieChart>> {
		const paramsObject: Record<string, string> = {
			startDate,
			mode: this._mapModeToPieApi(mode)
		};
		if (endDate) paramsObject["endDate"] = endDate;

		const params = new HttpParams({
			fromObject: paramsObject
		});

		return this._communicationService
			.callService(this._dashboardCioOperations.getEmissionsShare.service, HttpMethods.GET, {
				params
			})
			.pipe(
				map((response: PieChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as PieChart, hasError: true }))
			);
	}

	/**
	 * Retrieves the details for the specified time range and scope.
	 *
	 * @param startDate - The start date for the time range.
	 * @param endDate - The end date for the time range.
	 * @param scope - The scope to retrieve the details for.
	 * @returns An Observable that emits a BarChart object.
	 */
	public getDetails(
		startDate: string,
		endDate: string | undefined,
		scope: string
	): Observable<BarChart> {
		const paramsObject: Record<string, string> = {
			startDate,
			scope
		};
		if (endDate) paramsObject["endDate"] = endDate;

		const params = new HttpParams({
			fromObject: paramsObject
		});

		return this._communicationService
			.callService(this._dashboardCioOperations.getDetails.service, HttpMethods.GET, {
				params
			})
			.pipe(
				map((response: BarChart) => this._mapBarChartDates(response)),
				catchError(() => of({} as BarChart))
			);
	}

	private _mapModeToPieApi(mode: CioMode): "SCOPE" | "DOMAIN" {
		return mode === "SCOPE" ? "SCOPE" : "DOMAIN";
	}

	private _mapBarChartDates(chart: BarChart): BarChart {
		return {
			...chart,
			barSeries:
				chart.barSeries?.map(serie => ({
					...serie,
					bars:
						serie.bars?.map(bar => ({
							...bar,
							name: prettifyISODate(bar.name)
						})) || []
				})) || []
		};
	}

	private _mapTrendLineToMixedChart(trendLine: TrendLine): MixedChart {
		const barSeries = trendLine.bars?.barSeries || [];
		const barSeriesMapped = barSeries.map(serie => ({
			name: serie.name,
			type: "bar" as const,
			values: (serie.bars || []).map(bar => ({
				...bar,
				name: prettifyISODate(bar.name)
			}))
		}));

		const lineSeries = trendLine.trend
			? [
					{
						name: trendLine.trend.name,
						type: "line" as const,
						values: (trendLine.trend.linePoints || []).map(point => ({
							...point,
							name: prettifyISODate(point.name)
						}))
					}
				]
			: [];

		return {
			title: "",
			series: [...barSeriesMapped, ...lineSeries]
		};
	}
}
